import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AssessmentSetup } from '../components/features/assessment/AssessmentSetup';
import { ProgressModal } from '../components/ui/ProgressModal';
import { Industry, Role, QuestionSet, ProgressModalData } from '../types';
import { loadProgress, clearProgress } from '../utils/assessmentStorage';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [showAssessmentSetup, setShowAssessmentSetup] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressModalData, setProgressModalData] = useState<ProgressModalData | null>(null);
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);

  // Check for saved progress on mount
  useEffect(() => {
    const savedProgress = loadProgress();
    
    if (savedProgress && savedProgress.data.step1.industry) {
      // Prepare modal data
      const modalData: ProgressModalData = {
        industry: savedProgress.data.step1.industry?.name || 'Unknown',
        role: savedProgress.data.step2.role?.name || 'Not selected',
        currentStep: savedProgress.currentStep,
        lastSaved: new Date(savedProgress.lastSaved),
        progress: savedProgress.data.step4.progress.total > 0 
          ? savedProgress.data.step4.progress 
          : undefined,
      };
      
      setProgressModalData(modalData);
      setShowProgressModal(true);
    } else {
      // No saved progress, show setup directly
      setShowAssessmentSetup(true);
      setHasLoadedProgress(true);
    }
  }, []);

  const handleContinueProgress = () => {
    setShowProgressModal(false);
    setShowAssessmentSetup(true);
    setHasLoadedProgress(true);
  };

  const handleStartNew = () => {
    clearProgress();
    setShowProgressModal(false);
    setShowAssessmentSetup(true);
    setHasLoadedProgress(true);
  };

  const handleSetupComplete = (industry: Industry, role: Role, questionSet: QuestionSet) => {
    // Store the assessment data in localStorage or context
    const assessmentData = {
      industry,
      role,
      questionSet,
      startedAt: new Date().toISOString()
    };
    
    localStorage.setItem('currentAssessment', JSON.stringify(assessmentData));
    
    // Clear autosave progress since assessment is complete
    clearProgress();
    
    // Navigate to the assessment page
    navigate('/assessment');
  };

  // Show progress modal if we have saved progress
  if (showProgressModal && progressModalData) {
    return (
      <ProgressModal
        data={progressModalData}
        onContinue={handleContinueProgress}
        onStartNew={handleStartNew}
        isOpen={showProgressModal}
      />
    );
  }

  if (showAssessmentSetup && hasLoadedProgress) {
    return (
      <AssessmentSetup 
        onSetupComplete={handleSetupComplete}
        shouldLoadProgress={true}
      />
    );
  }

  // Loading state while checking for progress
  return (
    <div style={{ 
      maxWidth: '100%',
      margin: '0 auto', 
      padding: '1rem',
      textAlign: 'center' 
    }}>
      <h1 style={{ fontSize: 'clamp(1.5rem, 5vw, 2rem)' }}>Welcome to Process Optimizer</h1>
      <p style={{ fontSize: 'clamp(0.9rem, 3vw, 1rem)' }}>Loading...</p>
    </div>
  );
};

export default HomePage;
